/**
 * notificationModalStore.ts
 *
 * Single source of truth for "which modal should the notification flow
 * open?" — replaces the old lead-only store now that several notification
 * typeIDs deep-link into a detail modal (lead, payment, appointment, note,
 * survey response). The host component `NotificationModalHost` renders the
 * appropriate modal based on the active target.
 *
 * Why a single discriminated state instead of one store per modal:
 *   - Only one detail modal should ever be open at once (just like the
 *     legacy popup flow).
 *   - Every notification click ultimately routes through this store, so
 *     keeping the discriminator centralized makes it trivial to add new
 *     target kinds later (e.g. consult ready → consult modal).
 */
import { create } from 'zustand';

export type NotificationModalTarget =
  | { kind: 'lead'; leadId: number }
  | { kind: 'payment'; patientId: number; paymentId: number }
  | { kind: 'appointment'; appointmentId: number }
  | { kind: 'note'; patientId: number; noteId: number }
  | { kind: 'surveyResponse'; patientId: number; responseId: number };

interface NotificationModalState {
  active: NotificationModalTarget | null;
}

interface NotificationModalActions {
  open: (target: NotificationModalTarget) => void;
  close: () => void;
}

export type NotificationModalStore = NotificationModalState & NotificationModalActions;

export const useNotificationModalStore = create<NotificationModalStore>()((set) => ({
  active: null,
  open: (target) => set({ active: target }),
  close: () => set({ active: null }),
}));

/**
 * Imperative helper for non-React callers (e.g. toast onClick handlers
 * registered from inside `useNotificationPopups`). Matches the `toast`
 * helper pattern in toastStore.ts.
 */
export const notificationModal = {
  open: (target: NotificationModalTarget) => useNotificationModalStore.getState().open(target),
  close: () => useNotificationModalStore.getState().close(),
};
